/**
 * „Wie steht das Gebäude?" als Bildauswahl (xlsx Z. 7, Punkt 2).
 *
 * Vorher ein `<select>` mit drei Fachwörtern. „Angebaut" ist aber genau der
 * Fall, den Laien falsch einordnen (Garage am Haus? Reihenmittelhaus?) —
 * deshalb zeigt jede Karte die Situation im Bild: Freiraum auf beiden Seiten,
 * Berührung mit dem Nachbargebäude, oder ein neutraler Platzhalter.
 *
 * Gespeichert wird unverändert der String aus `STELLUNG_OPTIONS`
 * („freistehend" / „angebaut" / „sonstiges"); Bestandsdaten und Vertragsmaske
 * lesen genau diese Werte.
 *
 * Bildsprache und Tastaturmuster wie im `BauartklassePicker`: viewBox
 * `0 0 100 80`, Grundlinie bei y=74, Farben aus `ILLU`, Radiogroup mit
 * Roving-Tabindex.
 */
import { useRef } from "react";
import type { KeyboardEvent } from "react";
import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { BRAND } from "../risikoanalyseStep/tokens";
import { STELLUNG_OPTIONS } from "./enums";
import { ILLU } from "./illustrationTokens";

const useStyles = makeStyles({
  // Umbruchpunkt 720 px wie in den Nachbarschritten. Quadratisch werden die
  // Karten erst im Raster: eine bildschirmbreite Karte wäre auf dem Handy ein
  // 340-px-Klotz, der die Frage darunter aus dem Sichtfeld schiebt.
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: "14px",
    "@media (min-width: 720px)": {
      gridTemplateColumns: "repeat(3, 1fr)",
    },
  },
  karte: {
    cursor: "pointer",
    appearance: "none",
    backgroundColor: BRAND.white,
    backgroundImage: "none",
    textAlign: "center",
    padding: "0",
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    overflow: "hidden",
    ...shorthands.border("2px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("12px"),
    transitionProperty: "border-color, transform, box-shadow",
    transitionDuration: "150ms",
    "@media (min-width: 720px)": {
      aspectRatio: "1 / 1",
    },
    ":hover": {
      ...shorthands.borderColor(BRAND.purple),
      transform: "translateY(-2px)",
      boxShadow: "0 8px 16px rgba(45,20,43,0.08)",
    },
    ":focus-visible": {
      ...shorthands.outline("2px", "solid", BRAND.purple),
      outlineOffset: "2px",
    },
  },
  karteAktiv: {
    ...shorthands.borderColor(BRAND.purple),
    boxShadow: `0 0 0 4px ${BRAND.purple}22`,
  },
  bild: {
    flexGrow: 1,
    backgroundColor: ILLU.headerBg,
    paddingTop: "16px",
    paddingBottom: "16px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  // Spürbar größer als die Bauartklassen-Karten (dort 100x80): die Stellung ist
  // eine Ja-/Nein-Entscheidung am Bild, keine Detailunterscheidung.
  svg: {
    width: "116px",
    height: "93px",
    "@media (min-width: 480px)": {
      width: "130px",
      height: "104px",
    },
  },
  koerper: {
    paddingTop: "12px",
    paddingBottom: "14px",
    paddingLeft: "12px",
    paddingRight: "12px",
    display: "flex",
    flexDirection: "column",
    rowGap: "4px",
  },
  titel: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1.1rem",
    color: BRAND.ink,
  },
  hinweis: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.82rem",
    lineHeight: 1.35,
    color: BRAND.muted,
  },
});

/** Haus mit sichtbarem Freiraum und Abstandsmarken auf beiden Seiten. */
const Freistehend = ({ className }: { className?: string }) => (
  <svg className={className} width="100" height="80" viewBox="0 0 100 80" fill="none" aria-hidden="true" focusable="false">
    <path d="M4 74h92" stroke={ILLU.roofStroke} strokeWidth="1.5" strokeLinecap="round" />
    <path d="M32 44 50 28l18 16z" fill={ILLU.roofFill} stroke={ILLU.roofStroke} strokeWidth="1.5" strokeLinejoin="round" />
    <rect x="36" y="44" width="28" height="30" fill={ILLU.wallFill} stroke={ILLU.wallStroke} strokeWidth="1.5" />
    <rect x="46" y="58" width="8" height="16" fill={ILLU.glassFill} stroke={ILLU.jointStroke} strokeWidth="0.8" />
    <g stroke={ILLU.jointStroke} strokeWidth="1" fill="none">
      <path d="M12 46v28M88 46v28" strokeDasharray="4 3" />
      <path d="M14 62h20M66 62h20" />
      <path d="M18 59l-4 3 4 3M30 59l4 3-4 3M70 59l-4 3 4 3M82 59l4 3-4 3" />
    </g>
  </svg>
);

/**
 * Dasselbe Haus, rechts bündig an einem angeschnittenen Nachbargebäude. Der
 * Nachbar ist gedämpft (`headerBg`/`jointStroke`) und läuft aus dem Bild —
 * sonst läse sich die Karte als „zwei Gebäude gehören zum Objekt".
 */
const Angebaut = ({ className }: { className?: string }) => (
  <svg className={className} width="100" height="80" viewBox="0 0 100 80" fill="none" aria-hidden="true" focusable="false">
    <path d="M4 74h92" stroke={ILLU.roofStroke} strokeWidth="1.5" strokeLinecap="round" />
    <rect x="66" y="34" width="36" height="40" fill={ILLU.headerBg} stroke={ILLU.jointStroke} strokeWidth="1.2" />
    <path d="M72 44h24M72 54h24M72 64h24" stroke={ILLU.jointStroke} strokeWidth="1" strokeDasharray="3 3" />
    <path d="M22 44 44 28l22 16z" fill={ILLU.roofFill} stroke={ILLU.roofStroke} strokeWidth="1.5" strokeLinejoin="round" />
    <rect x="26" y="44" width="40" height="30" fill={ILLU.wallFill} stroke={ILLU.wallStroke} strokeWidth="1.5" />
    <rect x="40" y="58" width="8" height="16" fill={ILLU.glassFill} stroke={ILLU.jointStroke} strokeWidth="0.8" />
    <path d="M66 34v40" stroke={ILLU.roofStroke} strokeWidth="1.5" />
  </svg>
);

/**
 * Neutraler Platzhalter: gestrichelter Umriss mit drei Punkten. Bewusst **kein**
 * Haus mit Fragezeichen — das läse sich als „unbekannter Gebäudetyp" statt als
 * „andere Stellung".
 */
const Sonstiges = ({ className }: { className?: string }) => (
  <svg className={className} width="100" height="80" viewBox="0 0 100 80" fill="none" aria-hidden="true" focusable="false">
    <path d="M4 74h92" stroke={ILLU.roofStroke} strokeWidth="1.5" strokeLinecap="round" />
    <rect
      x="26"
      y="30"
      width="48"
      height="44"
      rx="4"
      fill={ILLU.headerBg}
      stroke={ILLU.jointStroke}
      strokeWidth="1.5"
      strokeDasharray="6 4"
    />
    <g fill={ILLU.roofStroke}>
      <circle cx="38" cy="52" r="3.4" />
      <circle cx="50" cy="52" r="3.4" />
      <circle cx="62" cy="52" r="3.4" />
    </g>
  </svg>
);

type Stellung = (typeof STELLUNG_OPTIONS)[number];

const KARTEN: ReadonlyArray<{
  wert: Stellung;
  titel: string;
  hinweis: string;
  Bild: (p: { className?: string }) => JSX.Element;
}> = [
  {
    wert: "freistehend",
    titel: "Freistehend",
    hinweis: "Ringsum frei — kein eigener Anbau und kein Nachbargebäude schließt an",
    Bild: Freistehend,
  },
  {
    wert: "angebaut",
    titel: "Angebaut",
    hinweis:
      "Ein eigener Anbau (z. B. Garage, Wintergarten) oder ein Nachbargebäude schließt direkt an",
    Bild: Angebaut,
  },
  {
    wert: "sonstiges",
    titel: "Sonstiges",
    hinweis: "Weder eindeutig freistehend noch angebaut",
    Bild: Sonstiges,
  },
];

type Props = {
  value: string;
  onChange: (naechste: string) => void;
};

const StellungPicker = ({ value, onChange }: Props) => {
  const styles = useStyles();
  const refs = useRef<Array<HTMLButtonElement | null>>([]);

  // Roving Tabindex: die gewählte Karte ist tabbar. Bei leerem oder unbekanntem
  // Wert fällt der Fokus auf die erste Karte — sonst wäre die Gruppe per
  // Tastatur gar nicht erreichbar.
  const gewaehlt = KARTEN.findIndex((k) => k.wert === value);
  const tabIndexOf = (i: number) => (i === (gewaehlt >= 0 ? gewaehlt : 0) ? 0 : -1);

  const handleKeyDown = (e: KeyboardEvent<HTMLButtonElement>, i: number) => {
    const letzter = KARTEN.length - 1;
    let ziel = -1;
    let pfeil = false;
    if (e.key === "ArrowRight" || e.key === "ArrowDown") {
      ziel = i === letzter ? 0 : i + 1;
      pfeil = true;
    } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
      ziel = i === 0 ? letzter : i - 1;
      pfeil = true;
    } else if (e.key === "Home") ziel = 0;
    else if (e.key === "End") ziel = letzter;
    if (ziel < 0) return;
    e.preventDefault();
    // Ohne Vorauswahl steht der Fokus auf Karte 0, gewählt ist aber nichts —
    // ein Weiterwandern würde „freistehend" stillschweigend überspringen.
    const naechste = pfeil && gewaehlt < 0 ? i : ziel;
    refs.current[naechste]?.focus();
    onChange(KARTEN[naechste].wert);
  };

  return (
    <div className={styles.grid} role="radiogroup" aria-label="Stellung des Gebäudes">
      {KARTEN.map((k, i) => {
        const aktiv = value === k.wert;
        const Bild = k.Bild;
        return (
          <button
            type="button"
            key={k.wert}
            ref={(el) => {
              refs.current[i] = el;
            }}
            role="radio"
            aria-checked={aktiv}
            tabIndex={tabIndexOf(i)}
            className={mergeClasses(styles.karte, aktiv && styles.karteAktiv)}
            onClick={() => onChange(k.wert)}
            onKeyDown={(e) => handleKeyDown(e, i)}
          >
            <span className={styles.bild}>
              <Bild className={styles.svg} />
            </span>
            <span className={styles.koerper}>
              <span className={styles.titel}>{k.titel}</span>
              <span className={styles.hinweis}>{k.hinweis}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
};

export default StellungPicker;
